/* Мост загрузки записи для движка шаблона (engine-kit, план движков §5.5 и §6 «Consent and upload»). Рамка BotCore.upload проверяет тип и
   размер по строке accept SV и отдаёт файл сюда (onPick); дальше — проверки VoiceOrderForm.pick по политике поля формы, один POST
   загрузки через сервис заказа и файл в рамке движка (put). Правила:
   - сервис заперт — причина словами; прав на материалы нет — лист согласия (consent), файл ждёт там;
   - тип (confirmAudioType, не для записи с микрофона) и accept → max_bytes → длина (readDurationMs) → не длиннее предела → не короче 1 с;
   - прежняя загрузка поля снимается, файл встаёт в рамку, затем ровно один svc.actions.upload(key, file);
   - транспорт не подтвердил (UploadTransportError): ждущий id остаётся в сервисе, файл — в рамке; resume() проверяет тот же id (waitForUpload);
   - окончательный отказ (UploadRejectedError, 4xx): файл уходит из рамки (drop: движок освобождает свой blob), загрузка поля снимается,
     причина — текст сервиса (uploadFailureText);
   - restore(uploadId) — запись из повтора («Изменить», «История»): как VoiceOrderForm.tsx:122-156 (me → превью → тип и размер → me → File →
     длина и волна), 30 с на всё; не вышло (решение владельца Q7) — загрузка поля снимается и запись надо загрузить заново.
   Устаревшие чтения отбрасывает счётчик поколений (новый выбор, cancel()). */
import { endpoints } from '../../../api';
import { uiFormat, uiText } from '../../../i18n';
import { sampleFile } from '../../clone/sample-file';
import { recordingWav } from '../../tts/recording-wav';
import { filePeaks, mmss } from '../../voice/voice-model';
import { uploadFailureText, type OrderDerived, type OrderService, type OrderSnapshot } from './order-service';

/** Политика поля записи из формы (ключ, типы, предел длины и размера) и строка ограничений рамки */
export interface AudioLimits {
  key: string;
  accept: string[];
  maxMs: number;
  maxBytes: number;
  note: string;
}
/** Запись в рамке движка (S.file шаблона): dur — секунды; peaks пустые — движок ставит ровную волну, пока настоящая не посчитана */
export interface EngineFile {
  name: string;
  size: number;
  url: string;
  dur: number;
  kind: 'audio';
  rec?: boolean;
  peaks: number[];
}
export interface UploadBridgeOptions {
  svc: OrderService;
  limits(): AudioLimits | null;
  owner(): number | undefined;
  /** Прав на материалы нет: лист согласия, файл ждёт в нём */
  consent(file: File, recorded: boolean): void;
  /** Причина словами в рамке (UP.say: дрожь и строка ограничений) */
  say(text: string): void;
  /** Файл в рамку движка (адрес blob с этого момента принадлежит движку) */
  put(f: EngineFile): void;
  /** Файл из рамки (null — рамка пустая) */
  drop(f: EngineFile | null): void;
  /** Волна файла в рамке посчитана позже */
  update?(f: EngineFile): void;
}
export interface UploadBridge {
  pick(file: File, recorded?: boolean): Promise<void>;
  /** «Продолжить загрузку»: тот же ждущий id (waitForUpload) или тот же файл, если слота ещё нет */
  resume(): Promise<void>;
  /** Запись с микрофона: в WAV (как VoiceOrderForm), затем pick без проверки типа */
  recorded(blob: Blob, maxMs: number): Promise<void>;
  restore(uploadId: string): Promise<void>;
  /** Выбранный файл ещё не подтверждён сервером (идёт загрузка или её надо продолжить) */
  pending(): boolean;
  /** Забыть выбранный файл и отбросить идущие чтения (убрали запись, экран закрылся) */
  cancel(): void;
}

/** Типы превью загрузки, которые рамка проигрывает (VoiceOrderForm AUDIO_TYPES) */
const PREVIEW_TYPES = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/webm'];
/** Текст сервиса для неподтверждённой загрузки (транспорт): только её можно продолжить тем же id */
const TRANSPORT = uploadFailureText(null);

function lockedText(s: OrderSnapshot, d: OrderDerived) {
  if (d.submissionBlocked) return s.integrityError || uiText('Проверяем доступность');
  if (s.intent) return uiText('Прежний заказ ещё не завершён. Сначала проверьте его результат.');
  return uiText(s.uploadingFile ? 'Загружаем файл' : 'Создаём');
}

export function createUploadBridge(o: UploadBridgeOptions): UploadBridge {
  const { svc } = o;
  let generation = 0;
  /** Выбранный файл до подтверждения загрузки */
  let held: { file: File; f: EngineFile } | null = null;
  let reading: AbortController | null = null;
  const live = () => {
    const s = svc.get();
    return { s, d: svc.derive(s) };
  };
  const refuse = (text: string) => o.say(uiText(text));

  /** Один upload сервиса для выбранного файла и разбор исхода */
  async function send(current: number) {
    const h = held,
      limits = o.limits();
    if (!h || !limits) return;
    const ok = await svc.actions.upload(limits.key, svc.get().uploadIds[limits.key] ? null : h.file);
    if (current !== generation || held !== h) return;
    if (ok) {
      held = null;
      return;
    }
    /* false — загрузка не началась (сервис заперт) или прервана: файл ждёт «Продолжить загрузку» */
    if (ok === false) return;
    /* без текста — запрос прервали (кадр сняли): это не окончательный ответ */
    const { error } = svc.get();
    if (!error || error === TRANSPORT) return;
    held = null;
    svc.actions.clearUpload(limits.key);
    o.drop(h.f);
    o.say(error);
  }

  async function pick(raw: File, recorded = false) {
    const limits = o.limits();
    if (!limits) return;
    const { s, d } = live();
    if (d.locked) return o.say(lockedText(s, d));
    if (!s.rights) return o.consent(raw, recorded);
    const current = ++generation;
    reading?.abort();
    reading = null;
    let chosen: File;
    try {
      chosen = recorded ? raw : sampleFile.confirmAudioType(raw);
    } catch {
      return o.say(limits.note);
    }
    if (!limits.accept.includes(chosen.type)) return o.say(limits.note);
    if (chosen.size > limits.maxBytes) return o.say(uiFormat('Файл больше {0} МБ, загрузите меньше', Math.round(limits.maxBytes / 1024 / 1024)));
    const ms = await sampleFile.readDurationMs(chosen).catch(() => null);
    if (current !== generation) return;
    if (!ms) return refuse('Файл не читается, загрузите другой');
    if (ms > limits.maxMs) return o.say(uiFormat('Запись длиннее {0}, загрузите короче', mmss(limits.maxMs / 1000)));
    if (ms < 1000) return refuse('Запись слишком короткая');
    const now = live();
    if (now.d.locked) return o.say(lockedText(now.s, now.d));
    if (now.s.uploadIds[limits.key] || now.s.inputs[limits.key]) svc.actions.clearUpload(limits.key);
    const f: EngineFile = { name: chosen.name, size: chosen.size, url: URL.createObjectURL(chosen), dur: ms / 1000, kind: 'audio', rec: recorded, peaks: [] };
    held = { file: chosen, f };
    o.put(f);
    void filePeaks(chosen).then(wave => {
      if (!wave || current !== generation) return;
      f.peaks = wave.peaks;
      o.update?.(f);
    });
    await send(current);
  }

  async function resume() {
    if (!held) return;
    const { s, d } = live();
    if (d.locked) return o.say(lockedText(s, d));
    await send(generation);
  }

  async function recorded(blob: Blob, maxMs: number) {
    const current = generation;
    let wav: File;
    try {
      wav = await recordingWav(blob instanceof File ? blob : new File([blob], 'recording', { type: blob.type }), maxMs);
    } catch {
      return refuse('Не удалось прочитать запись, попробуйте ещё раз');
    }
    if (current !== generation) return;
    await pick(new File([wav], uiText('Запись с микрофона') + '.wav', { type: 'audio/wav' }), true);
  }

  async function restore(uploadId: string) {
    const limits = o.limits(),
      owner = o.owner();
    if (!limits || !owner) return;
    const current = ++generation;
    reading?.abort();
    const request = new AbortController();
    reading = request;
    let timedOut = false;
    const deadline = window.setTimeout(() => {
      timedOut = true;
      request.abort();
    }, 30000);
    /* запись поля сменили или убрали, пока читали превью */
    const gone = () => current !== generation || svc.get().inputs[limits.key] !== uploadId;
    try {
      const me = await endpoints.me();
      if (me.user.id !== owner) throw new Error('owner_changed');
      const blob = await endpoints.uploadPreview(uploadId, request.signal);
      if (request.signal.aborted) throw new Error('aborted');
      if (!PREVIEW_TYPES.includes(blob.type) || blob.size > limits.maxBytes) throw new Error('audio_preview_invalid');
      const again = await endpoints.me();
      if (request.signal.aborted) throw new Error('aborted');
      if (again.user.id !== owner) throw new Error('owner_changed');
      const file = new File([blob], uiText('Запись'), { type: blob.type });
      const ms = await sampleFile.readDurationMs(file, request.signal).catch(() => null);
      const wave = await filePeaks(file, undefined, request.signal);
      if (request.signal.aborted) throw new Error('aborted');
      const durationMs = ms ?? wave?.durationMs ?? null;
      if (!durationMs) throw new Error('audio_preview_unreadable');
      if (gone()) return;
      o.put({ name: file.name, size: file.size, url: URL.createObjectURL(file), dur: durationMs / 1000, kind: 'audio', peaks: wave?.peaks ?? [] });
    } catch {
      /* cancel() и новый выбор — молча; дедлайн и любой отказ — загрузить заново, прежний id не используется */
      if (current !== generation || (request.signal.aborted && !timedOut) || svc.get().inputs[limits.key] !== uploadId) return;
      svc.actions.clearUpload(limits.key);
      o.drop(null);
      refuse('Не удалось открыть запись для прослушивания. Загрузите её заново.');
    } finally {
      clearTimeout(deadline);
      if (reading === request) reading = null;
    }
  }

  return {
    pick,
    resume,
    recorded,
    restore,
    pending: () => held !== null,
    cancel() {
      generation++;
      held = null;
      reading?.abort();
      reading = null;
    },
  };
}
